import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, from, Observable, of, ReplaySubject } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ConfigService } from './config.service';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { filter } from 'rxjs/internal/operators/filter';
import { take } from 'rxjs/internal/operators/take';
import { HttpClient } from '@angular/common/http';
import { Permissions } from './permissions.model';
import { get as _get } from 'lodash';
import { PopupService } from 'shared/popup-service/popup.service';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ChargingStationPermissions, LicensingApiService } from '@kempoweroyj/ngx-licensing-api-client';
import { AuthTokenService } from '@kempowersw/ngx-auth-token-service';
import { AuthTokenService as AuthTokenServiceOyj } from '@kempoweroyj/ngx-auth-token-service';
import { IamOrganizationsApiService } from '@kempoweroyj/ngx-iam-organizations-api-client';
import { LocalStoreService } from './local-store.service';
import { EnvironmentService } from './environment.service';

const LOCAL_STORAGE_PATH = 'kempower-csms-auth';

export interface Profile {
    aud: string;
    /**
     * Email address.
     */
    email: string;
    /**
     * If true, email is verified.
     */
    emailVerified: boolean;
    exp: number;
    iat: number;
    iss: string;
    /**
     * User's display name
     */
    name: string;
    nickname: string;
    nonce: string;
    picture: string;
    /**
     * Auth0 id
     */
    sub: string;
    updatedAt: string;

    /**
     * App metadata
     */
    tenantId: string;
    userRole: AuthUserRole;
    systemRoles?: string[];
    originalTenantId?: string;
    originalUserRole?: AuthUserRole;
    originalSystemRoles?: string[];
    impersonated?: boolean;

    /**
     * Permissions
     */
    permissions: Permissions;
}

export type AccessString = string | string[];
export type AccessCondition = 'AND' | 'OR';
export type PermissionsHelper = Permissions & {
    hasAccess(accessString: AccessString, condition?: AccessCondition): boolean;
};
export type OrganizationTab = 'BASIC' | 'BILLING';
export interface UserFromUsersAPI {
    createdAt: string;
    email: string;
    modifiedAt: string;
    name: string;
    position: string;
    department: string;
    tenantId: string;
    secondaryTenantIds: string[];
    picture: string;
    systemRoles: string[];
    userId: string;
    language: string;
    organizationName: string;
}

export type AuthUserRole = 'emsp' | 'cpo' | 'maintenance';
export type AuthSystemRole =
    | 'securityAdmin'
    | 'systemAdmin'
    | 'default'
    | 'orgAdmin'
    | 'maintenance'
    | 'readonly'
    | 'notifications'
    | 'factory'
    | 'device'
    | 'chargerDiagnostics';

export interface AuthPermissions {
    isSystemAdmin?: boolean;
    isOrganizationAdmin?: boolean;
    isReadonly?: boolean;
    isMaintenance?: boolean;
    isDiagnostics?: boolean;
}

export interface Organization {
    name: string;
    tenantId: string;
    type?: 'A' | 'B';
    sharedTimestamp?: string;
    customerId?: string;
}

export type OriginalAuthInfo = Pick<
    Profile,
    'originalTenantId' | 'originalUserRole' | 'originalSystemRoles' | 'impersonated'
>;

export const StationPermissionsPromiseCache: {
    [stationId: string]: Promise<ChargingStationPermissions>;
} = {};

@Injectable({ providedIn: 'root' })
export class AuthService {
    get jwt(): string {
        return this.localStore.get()?.jwt;
    }

    /** ngx api pkgs gets from AuthService jwt token and restApiUrl. This getter ensures the ngx api service constructed correctly */
    get restApiUrl() {
        return this.authToken.restApiUrl;
    }
    get profile() {
        return this.profile$.value;
    }

    /** User should be cached every time it's fetched from the iam/users */
    cache: { [userSub: string]: UserFromUsersAPI } = {};
    profile$ = new BehaviorSubject<Profile>(null);
    authenticated$ = new BehaviorSubject(false);
    permissions$ = new ReplaySubject<PermissionsHelper>(1);
    get permissionsPromise() {
        return this.permissions$.pipe(take(1)).toPromise();
    }
    /** If true, user is being redirected to login page */
    redirectInProgress = false;

    private readonly localStore = new LocalStoreService<LocallyStoredAuth>(LOCAL_STORAGE_PATH);
    private jwtHelperService = new JwtHelperService();

    constructor(
        private readonly http: HttpClient,
        private readonly configService: ConfigService,
        private readonly router: Router,
        private readonly popupService: PopupService,
        private readonly licensingApi: LicensingApiService,
        private readonly authToken: AuthTokenService,
        private readonly authTokenOyj: AuthTokenServiceOyj,
        private readonly iamOrganizationsApi: IamOrganizationsApiService,
        private readonly environmentService: EnvironmentService
    ) {}

    async getStationPermissions(stationId: string) {
        let promise = StationPermissionsPromiseCache[stationId];
        if (!promise) {
            // if promise not cached, get up-to-date one
            promise = StationPermissionsPromiseCache[stationId] = this.licensingApi
                .getChargingStationPermissionsStation(stationId, {}, { headers: { 'Cache-Control': 'max-age=0' } })
                .then((permsRes) => {
                    return permsRes.permissionSets.find((ps) => ps.stationIds.includes(stationId));
                });
        }

        return promise;
    }

    /** Cached permissions response */
    private _permissionsPromise: Promise<Permissions>;

    /** Fetch a permissions object that describes the permissions that even one station has */
    private async getPermissionsAnyStation() {
        if (!this._permissionsPromise) {
            // if promise not cached, get up-to-date one
            this._permissionsPromise = this.licensingApi
                .getChargingStationPermissions({ combined: true }, { headers: { 'Cache-Control': 'max-age=0' } })
                .then((permsRes) => {
                    return permsRes.permissionSets[0].permissions;
                });
        }

        return this._permissionsPromise;
    }

    /**
     * [PRIVATE] use permissions$ and hasAccessTo$ instead
     *
     * Checks whether user has access to specific item / items
     *
     * TIP: If the user has systemAdmin role then most likely the user has permission from systemAdminRoleMask
     * and isSystemAdmin check is redundant with hasAccessTo.
     *
     * @param access Permission path to check, e.g. `fleet.chargingStation.delete`
     */
    private hasAccessTo(accessString: AccessString, condition: AccessCondition = 'AND', perms: Permissions): boolean {
        return accessHelper(this.getPermissions(), perms, accessString, condition);
    }

    /**
     *
     * Returns an observable showing whether user has access to specific item / items.
     *
     * Use this if the whole permissions object is not needed. Else, check permissions$
     *
     * NOTE: hasAccessTo$ will block other operations until fetch is done. If needed, pipe a `startWith(false)` in calling code. Here, it should be kept like this.
     */
    hasAccessTo$(accessString: AccessString, condition: AccessCondition = 'AND'): Observable<boolean> {
        const permissions$ = from(this.getPermissionsAnyStation()).pipe();
        return permissions$.pipe(
            map((permissions) => !!accessHelper(this.getPermissions(), permissions, accessString, condition)),
            distinctUntilChanged()
        );
    }

    hasAccessToStation$(
        _stationId: string,
        accessString: AccessString,
        condition: AccessCondition = 'AND'
    ): Observable<boolean> {
        // do not check station permissions anymore
        return this.hasAccessTo$(accessString, condition);
    }

    private my: OrganizationMyResponse;
    async getMy() {
        if (this.my) {
            return this.my;
        }

        // fetch..
        const url = `${this.configService.restApiUrl}/iam/organizations/my`;
        await this.userIsAuthed();

        const response = await this.http
            .get<OrganizationMyResponse>(url, {
                headers: { Authorization: `Bearer ${this.jwt}` },
            })
            .toPromise();

        return response;
    }

    async loginWithToken(token: string) {
        const profile = this.jwtHelperService.decodeToken(token) as Profile;

        await this.setAuth({
            jwt: token,
            profile,
        });
    }

    async setAuth(auth: LocallyStoredAuth) {
        auth.profile = removeProfileNamespaces(auth.profile);
        this.localStore.set(auth);
        this.authToken.jwt = auth.jwt;
        this.authTokenOyj.jwt = auth.jwt;
        this._permissionsPromise = undefined;
        await this.informLoggedIn(auth);
    }

    /**
     * Wait until user is authenticated, then resolve promise. Useful to know when e.g profile object, jwt string is ready for use.
     * @returns true whenever authenticated$ observable emits true.
     */
    async userIsAuthed() {
        return await this.authenticated$
            .pipe(
                distinctUntilChanged(),
                filter((authed) => authed),
                take(1)
            )
            .toPromise();
    }

    getPermissions(): AuthPermissions {
        const profile = this.profile;
        return {
            isSystemAdmin: (profile?.systemRoles || []).includes('systemAdmin'),
            isOrganizationAdmin: (profile?.systemRoles || []).includes('orgAdmin'),
            isMaintenance: (profile?.systemRoles || []).includes('maintenance'),
            isReadonly: (profile?.systemRoles || []).includes('readonly'),
            isDiagnostics: (profile?.systemRoles || []).includes('chargerDiagnostics'),
        };
    }

    getRolePermissions(): Permissions {
        return this.profile.permissions;
    }

    getOriginalAuthInfo(): OriginalAuthInfo {
        const originalInfo = {
            originalTenantId: this.profile?.originalTenantId,
            originalUserRole: this.profile?.originalUserRole,
            originalSystemRoles: this.profile?.originalSystemRoles,
            impersonated: this.profile?.impersonated,
        };
        return originalInfo;
    }

    logout() {
        this.localStore.set(null);
        this.authToken.jwt = null;
        this.authTokenOyj.jwt = null;
        this.profile$.next(undefined);
        this.authenticated$.next(false);

        // explicitly indicate that the authenticated$ observable is done
        // we cannot use authenticated$.next(false) because it will be false initially
        // other services might save state to local storage, this would lead to desync state
        this.authenticated$.complete();
        this._permissionsPromise = undefined;

        // Log out the user from Auth0
        const returnToUrl = new URL(window.location.href);
        returnToUrl.search = '';
        returnToUrl.pathname = '/';
        window.location.href = `https://${this.configService.config.auth.domain}/v2/logout?client_id=${encodeURIComponent(this.configService.config.auth.clientId)}&returnTo=${encodeURIComponent(
            returnToUrl.toString()
        )}`;
    }

    private async informLoggedIn(auth: LocallyStoredAuth) {
        this.profile$.next(auth.profile);

        if (this.profile.permissions == null) {
            const perms = await this.getPermissionsAnyStation();
            this.profile.permissions = perms;
            this.permissions$.next({
                ...perms,
                hasAccess: (accessString: AccessString, condition: AccessCondition = 'AND') =>
                    this.hasAccessTo(accessString, condition, perms),
            });
        }
        this.authenticated$.next(true);
    }

    async ensureLoggedIn() {
        if (this.redirectInProgress || window.location.pathname === '/auth-callback') {
            return;
        }

        const storedAuth = this.localStore.get();
        if (storedAuth && !this.jwtHelperService.isTokenExpired(storedAuth.jwt)) {
            this.authToken.jwt = storedAuth.jwt;
            this.authTokenOyj.jwt = storedAuth.jwt;
            await this.informLoggedIn(storedAuth);
        } else {
            await this.login();
        }
    }

    /**
     * Login with single-use code (part of Auth0 Authorization Code Flow).
     */
    async loginWithCode(code: string) {
        const callbackUrl = this._getCallbackUrl();
        const res = await this.iamOrganizationsApi.getAccessTokenWithCode({
            code,
            redirectUri: callbackUrl,
        });
        await this.loginWithToken(res.accessToken);
    }

    async checkJwtExpiry() {
        const storedAuth = this.localStore.get();
        if (storedAuth && this.jwtHelperService.isTokenExpired(storedAuth.jwt)) {
            await this.login();
        }
    }

    async login() {
        this.authenticated$.next(false);
        this.authenticated$.complete();
        const url = await this._getLoginUrl();
        this.redirectInProgress = true;
        window.location.href = url;
    }

    private async _getLoginUrl() {
        const clientId = this.configService.config.auth.clientId;
        const callbackUrl = this._getCallbackUrl();
        const origin = await firstValueFrom(this.environmentService.getAppOrigin$());
        const url =
            `https://${this.configService.config.auth.domain}/authorize?` +
            `response_type=code` +
            `&client_id=${encodeURIComponent(clientId)}` +
            `&redirect_uri=${encodeURIComponent(callbackUrl)}` +
            `&scope=${encodeURIComponent('openid profile email')}` +
            `&origin=${encodeURIComponent(origin)}`;
        return url;
    }

    private _getCallbackUrl() {
        const self = window.location.origin;
        const callbackUrl = `${self}/auth-callback?redirectUrl=${encodeURIComponent(window.location.href)}`;
        return callbackUrl;
    }

    private redirect(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        if (route.outlet === 'primary') {
            // Split the url with path which user had no access for. The zero index contains the path we want to redirect to
            const redirectToUrl = state.url.split(`/${route.routeConfig.path}`)[0];

            // Keep same queryParams for now
            this.router.navigate([redirectToUrl], { queryParams: route.queryParams });
        } else {
            // Dialog routing
            this.router.navigate([]);
        }
    }
    // Add this service to router as the canActivate gate.
    // Router will call this function for each route thus protected.
    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        const isAuthenticated = await this.authenticated$
            .pipe(
                filter((r) => {
                    return !!r;
                }),
                take(1)
            )
            .toPromise();

        // We are not authenticated -> no access
        if (!isAuthenticated) {
            return false;
        }

        // If route requires permissions
        let routePermissions = _get(route, ['data', 'requiredPermissions'], []);
        const accessCondition: AccessCondition = _get(route, ['data', 'accessCondition'], 'AND');
        const redirectToIfNoPermission: string = route?.data?.redirectToIfNoPermission;

        if (routePermissions != null) {
            if (!Array.isArray(routePermissions)) {
                // turns to array
                routePermissions = [routePermissions];
            }

            if (!routePermissions.length) {
                // bail out early if empty array
                return true;
            }

            let hasAccess = false;

            // Looping through routePermissions
            for (const perm of routePermissions) {
                hasAccess = this.hasAccessTo(perm, 'AND', this.profile.permissions);

                // 'AND'
                if (accessCondition === 'AND') {
                    // If NO ACCESS -> redirect immediately
                    if (!hasAccess && redirectToIfNoPermission) {
                        console.error('No required access', perm);
                        // eslint-disable-next-line no-console
                        console.log(`Redirecting to ${redirectToIfNoPermission}`);
                        return this.router.createUrlTree([redirectToIfNoPermission]);
                    } else if (!hasAccess) {
                        console.error('No required access', perm);
                        this.popupService.error(`No required access (${perm})`);

                        this.redirect(route, state);

                        return false;
                    } else {
                        // If ACCESS GRANTED -> continue through array
                        continue;
                    }
                } else if (accessCondition === 'OR') {
                    if (!hasAccess) {
                        // If ACCESS GRANTED -> continue through array
                        continue;
                    } else {
                        return true;
                    }
                }
            }

            // At end of routePermissions
            if (!hasAccess && redirectToIfNoPermission) {
                // redirect to another page
                console.error('No required access', routePermissions, accessCondition);
                // eslint-disable-next-line no-console
                console.log(`Redirecting to ${redirectToIfNoPermission}`);
                return this.router.createUrlTree([redirectToIfNoPermission]);
            } else if (!hasAccess) {
                // if still no access, redirect
                console.error('No required access', routePermissions, accessCondition);
                this.popupService.error(`No required access (${routePermissions.join(` ${accessCondition} `)})`);

                // redirect to parent if possible after 1 sec (to avoid infinite busy-loops)
                setTimeout(() => this.redirect(route, state), 1000);

                return false;
            } else {
                return true;
            }
        }
        return true;
    }

    async impersonate(body: ImpersonateRequest): Promise<any> {
        const url = `${this.configService.restApiUrl}/auth/impersonate`;
        try {
            const response = await this.http
                .post<ImpersonateResponse>(url, body, {
                    headers: { Authorization: `Bearer ${this.jwt}` },
                })
                .toPromise();

            await this.loginWithToken(response.token);
            const currentRoute = this.router.url;

            // trigger a refresh of the current route to reflect new permissions
            this.router.navigate(['/release-notes/chargeye'], { skipLocationChange: true }).then(() => {
                this.router.navigate([currentRoute], { replaceUrl: true });
            });
        } catch (e) {
            console.error('Error during KempowerCsmsApiAuth#impersonate', { url, body });
            throw e;
        }
    }
    async getAdminOrganization(orgId: string): Promise<Organization> {
        const url = `${this.configService.restApiUrl}/iam/admin/organizations/${encodeURIComponent(orgId)}`;
        try {
            const response = await this.http
                .get<any>(url, {
                    headers: { Authorization: `Bearer ${this.jwt}` },
                })
                .toPromise();
            return response;
        } catch (e) {
            console.error('Error during KempowerCsmsApiAuth#getAdminOrganization', { url });
            throw e;
        }
    }

    adminOrgCache$: { [orgId: string]: Promise<Organization> } = {};
    getAdminOrganizationWithCache(orgId: string): Promise<Organization> {
        if (!this.adminOrgCache$[orgId]) {
            this.adminOrgCache$[orgId] = this.getAdminOrganization(orgId).catch(() => undefined);
        }

        return this.adminOrgCache$[orgId];
    }

    navigateToAdminIamOrganizationPage(organizationId: string, tab?: OrganizationTab): void {
        window.open(
            `${window.location.origin}/admin/iam/organizations/id/${organizationId}${tab != null ? '/billing' : ''}`,
            '_blank'
        );
    }

    /**
     * @param userSub e.g. auth0|5f3e53ed9467120067123456
     * @returns URL for users picture. Note URL is taken from cache if it's cached
     */
    async getUserPicture(userSub: string): Promise<string> {
        const user = await this.getUser(userSub);
        return await user?.picture;
    }

    async getUser(userId: string) {
        if (!userId) {
            return undefined;
        }
        const userSubArr = userId?.split('|');

        const isAuth0 = userSubArr[0] === 'auth0';

        if (isAuth0) {
            userId = userSubArr[1];
        }

        if (this.cache[userId]) {
            return this.cache[userId];
        }
        const getUrl = `${this.configService.restApiUrl}/iam/users/${encodeURIComponent(userId)}`;

        const user = await this.http
            .get<any>(getUrl, {
                headers: {
                    Authorization: `Bearer ${this.jwt}`,
                },
            })
            .toPromise()
            .catch((e) => {
                if (e.status !== 404) {
                    console.error(e);
                }
            });

        this.cache[userId] = user;
        return user;
    }

    isKempowerUser$(): Observable<boolean> {
        return this.authenticated$.pipe(
            filter((authenticated) => authenticated),
            distinctUntilChanged(),
            switchMap(() => {
                // wait until authenticated (for user profile to be populated)
                const isKempowerUser = this.profile?.tenantId === 'TESTING';

                return of(isKempowerUser);
            })
        );
    }
    //check if there is a active license
    async isThereLicenses() {
        try {
            const res = await this.licensingApi.getActiveLicenses(); // fetch all active licenses
            if (res.licenses.length < 1) {
                return false;
            } else {
                return true;
            }
        } catch (e) {
            console.error(`Error while fetching licenses`, e);
            throw e;
        }
    }
    getTopSystemRole(): AuthSystemRole {
        const systemRoles = this.profile?.systemRoles;
        if (!systemRoles || systemRoles.length === 0) {
            return null;
        }

        const priority: AuthSystemRole[] = [
            'securityAdmin',
            'systemAdmin',
            'orgAdmin',
            'maintenance',
            'default',
            'chargerDiagnostics',
            'readonly',
            'notifications',
            'factory',
            'device',
        ];

        // Iterate through the priority list and return the first matching role.
        for (const role of priority) {
            if (systemRoles.includes(role)) {
                return role;
            }
        }

        // If none of the priority roles are found in systemRoles, return null.
        return null;
    }
}

export interface ImpersonateRequest {
    tenantId: string;
    userRole?: AuthUserRole;
    systemRoles?: AuthSystemRole[];
    roles?: string[];
}

export interface ImpersonateResponse {
    token: string;
}

export interface OrganizationMyResponse {
    /** Organization name */
    name: string;
    /** IANA tzId */
    timezone: string;
    tenantId: string;
    modifiedAt: string;
    contactPerson: {
        email: string;
        personName: string;
        phone: string;
    };
    address: object;
}

// tslint:disable-next-line: no-empty-interface
export interface PermissionsResponse extends Permissions {}

interface LocallyStoredAuth {
    jwt: string;
    profile: Profile;
}

/**
 * In Auth0 there is a configured Rule which outputs tenant and role
 * into the id_token (JWT token payload), but Auth0 enforces that
 * the added properties MUST be namespaced. So we do it,
 * but here we strip away the namespace.
 *
 * More information here: https://auth0.com/docs/api-auth/tutorials/adoption/scope-custom-claims
 */
const AUTH_NAMESPACE = 'https://auth.kempower.com/';
function removeProfileNamespaces(originalObj: any): Profile {
    return Object.keys(originalObj).reduce((obj, key) => {
        if (key.startsWith(AUTH_NAMESPACE)) {
            obj[key.slice(AUTH_NAMESPACE.length)] = originalObj[key];
        } else {
            obj[key] = originalObj[key];
        }
        return obj;
    }, {}) as Profile;
}

function accessHelper(
    systemRoles: AuthPermissions,
    permissions: Permissions,
    accessString: AccessString,
    condition: AccessCondition = 'AND'
): boolean {
    const isSystemAdmin = systemRoles.isSystemAdmin;
    const isOrganizationAdmin = systemRoles.isOrganizationAdmin;

    if (accessString === 'sysadmin') {
        return isSystemAdmin;
    }

    if (accessString === 'orgAdmin') {
        return isOrganizationAdmin;
    }

    if (Array.isArray(accessString)) {
        if (!condition || condition === 'AND') {
            for (const access of accessString) {
                let allow = _get(permissions, access, false);

                if (access === 'sysadmin') {
                    allow = isSystemAdmin;
                }

                if (access === 'orgAdmin') {
                    allow = isOrganizationAdmin;
                }

                if (!allow) {
                    return false;
                }
            }
            return true;
        } else if (condition === 'OR') {
            for (const access of accessString) {
                if (access === 'sysadmin') {
                    if (isSystemAdmin) {
                        return true;
                    }
                } else if (access === 'orgAdmin') {
                    if (isOrganizationAdmin) {
                        return true;
                    }
                } else if (_get(permissions, access, false)) {
                    return true;
                }
            }
            return false;
        } else {
            return false;
        }
    } else {
        return _get(permissions, accessString, false);
    }
}
